import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    Backdrop,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    FormControlLabel,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Snackbar,
    Alert,
    IconButton,
    useTheme,
    useMediaQuery,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from "@mui/material";
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { useParams } from "react-router-dom";
import { Logger } from "utils/Logger";
import { CollectionUser } from "modules/Interfaces/InstanUpdates/CollectionUsers";
import { handleApiError } from "utils/ErrorHandler";
import { apiClient } from "utils/ApiClient";
import { Add, Cancel, Save, Delete, CloudUpload, Edit, Visibility, Phone, Email as EmailIcon, Person, Search } from "@mui/icons-material";
import { InputAdornment } from "@mui/material";
import { COLLECTION_USERS_URL, GET_COLLECTION_SCHEMA_URL } from "envConfig";
import CsvManager from "../CsvManager/CsvManager";
import { DynamicBreadcrumbs } from "components/Breadcrumb";
import { DataTableWithDynamicSchema } from "components/DataTable";
import { CamelCaseToName } from "utils/CamelCaseToName";
import { useModulePermissions } from "hooks/useModulePermissions";
import {
    SWIPE_ACTION_WIDTH_PX,
    SWIPE_DIRECTION_LOCK_PX,
    clampSwipeOffset,
    computeSnappedOffset,
} from "utils/iosSwipeRow";

export const CollectionUsers: React.FC = () => {
    const { collectionName } = useParams<{ collectionName: string }>();
    const [loading, setLoading] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [collectionSchema, setCollectionSchema] = useState<any>(null); // To store schema passed up from DataTable
    const [currentCollectionUser, setCurrentCollectionUser] = useState<Partial<CollectionUser> | null>(null);
    const [collectionUsersDataRefreshKey, setCollectionUsersDataRefreshKey] = useState(0); // To trigger DataTable refresh
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
    
    // Delete confirmation dialog state
    const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState<any>(null);

    // Mobile responsive state
    const theme = useTheme();
    const tileUserIconColor = theme.palette.heading?.main
        ?? theme.palette.dashboard?.heroTileForegroundColor
        ?? theme.palette.secondary.main;
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

    /** Mobile/tablet: iOS-style swipe row — underlay + translateX with snap */
    const [mobileSwipeRow, setMobileSwipeRow] = useState<{
        userId: string;
        offset: number;
        dragging: boolean;
    } | null>(null);
    const swipeSessionRef = useRef<{
        userId: string;
        startX: number;
        startY: number;
        baseOffset: number;
        lock: 'h' | 'v' | null;
    } | null>(null);
    const latestSwipeOffsetRef = useRef(0);
    
    // Validation state
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    // View mode toggle
    const VIEWMODE_STORAGE_KEY = `collection-users-view-mode-${collectionName}`;
    const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
        const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
        if (stored === 'tile' || stored === 'table') return stored;
        return 'tile';
    });
    const [tileData, setTileData] = useState<any[]>([]);
    const [tileLoading, setTileLoading] = useState(false);
    const [tileSearchQuery, setTileSearchQuery] = useState('');
    const TILE_PAGE_SIZE = 20;
    const [tileVisibleCount, setTileVisibleCount] = useState(TILE_PAGE_SIZE);

    const fetchTileData = useCallback(async () => {
        if (!collectionName) return;
        try {
            setTileLoading(true);
            const res = await apiClient.get(`/iu/ds/cmp/set/entry/${collectionName}`);
            setTileData(Array.isArray(res.data) ? res.data : []);
        } catch {
            setTileData([]);
        } finally {
            setTileLoading(false);
        }
    }, [collectionName]);

    useEffect(() => {
        if (viewMode === 'tile') {
            fetchTileData();
        }
    }, [viewMode, collectionUsersDataRefreshKey, fetchTileData]);

    useEffect(() => {
        if (viewMode === 'tile' && !collectionSchema && collectionName) {
            apiClient.get(`/iu/ds/cmp/set/entry/schema/${collectionName}`)
                .then((res: any) => { if (res.data) setCollectionSchema(res.data); })
                .catch(() => {});
        }
    }, [viewMode, collectionSchema, collectionName]);

    const filteredTileData = useMemo(() => {
        const q = tileSearchQuery.toLowerCase().trim();
        if (!q) return tileData;
        return tileData.filter((u: any) =>
            [u.firstName, u.lastName, u.primaryMobile, u.primaryEmail]
                .filter(Boolean)
                .some((v: string) => String(v).toLowerCase().includes(q))
        );
    }, [tileData, tileSearchQuery]);

    useEffect(() => {
        setTileVisibleCount(TILE_PAGE_SIZE);
    }, [tileSearchQuery]);

    // Helper function to show snackbar messages
    const showMessage = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    // Get user permissions
    const { getInstantUpdateRole } = useModulePermissions();
    const userRole = getInstantUpdateRole();
    
    // Determine permissions based on role
    const canEdit = userRole === 'admin';
    const canDelete = userRole === 'admin';
    const canCreate = userRole === 'admin';
    const canManageCsv = userRole === 'admin';
    
    // Apply restrictions for "Consent_Records" dataset
    const isConsentRecordsDataset = collectionName === "Consent_Records";
    const canEditWithRestrictions = canEdit && !isConsentRecordsDataset;
    const canDeleteWithRestrictions = canDelete && !isConsentRecordsDataset;

    // Fields to exclude from dynamic rendering (system/internal fields)
    const excludedFields = ['_id', 'createdAt', 'updatedAt', '__v'];



    // Callback to receive the schema from DataTableWithDynamicSchema once it's loaded
    const handleSchemaLoaded = useCallback((schema: any) => {
        setCollectionSchema(schema);
        Logger.debug("CollectionUsers.tsx : handleSchemaLoaded: Schema received from DataTable:", schema);
    }, []);

    // Function to open the Add/Edit dialog
    const openDialog = useCallback((collectionUserRecord?: Record<string, any>) => {
        setMobileSwipeRow(null);
        if (collectionUserRecord) {
            // Editing existing user
            setCurrentCollectionUser(collectionUserRecord);
            setIsEditing(true);
            setIsViewMode(false);
        } else {
            // Adding new user: construct default user based on fetched schema
            const defaultUser: Record<string, any> = {};
            if (collectionSchema && collectionSchema.paths) {
                Object.keys(collectionSchema.paths).forEach((key) => {
                    if (excludedFields.includes(key)) return; // Skip excluded fields

                    const fieldSchema = collectionSchema.paths[key];
                    switch (fieldSchema?.instance) {
                        case "Number":
                            defaultUser[key] = 0; // Default to 0 for numbers
                            break;
                        case "Boolean":
                            defaultUser[key] = false; // Default to false for booleans
                            break;
                        case "Date":
                            defaultUser[key] = new Date().toISOString().slice(0, 16); // Default to current datetime string for datetime-local input
                            break;
                        case "String":
                        default:
                            defaultUser[key] = ""; // Default to empty string for others
                            break;
                    }
                });
            }
            

            setCurrentCollectionUser(defaultUser);
            setIsEditing(false);
            setIsViewMode(false);
        }
        setDialogOpen(true);
    }, [collectionSchema]); // Dependency: collectionSchema is needed to build default user

    // Function to open the View dialog (read-only mode)
    const openViewDialog = useCallback((collectionUserRecord: Record<string, any>) => {
        setMobileSwipeRow(null);
        setCurrentCollectionUser(collectionUserRecord);
        setIsViewMode(true);
        setIsEditing(false);
        setDialogOpen(true);
    }, []);



    // Memoized delete function to pass to DataTable
    const deleteCollectionUser = useCallback(async (record: any) => {
        setRecordToDelete(record); // Set the record to delete
        setDeleteConfirmDialogOpen(true); // Open the confirmation dialog
    }, []);

    // Memoized close dialog function
    const closeDialog = useCallback(() => {
        setDialogOpen(false);
        setCurrentCollectionUser(null);
        setIsViewMode(false);
        setValidationErrors({}); // Clear validation errors when dialog closes
    }, []);

    // Memoized save user function
    const saveCollectionUser = useCallback(async () => {
        if (!currentCollectionUser) return;
        try {
            setLoading(true);
            
            const { _id, ...payload } = currentCollectionUser; // Destructure _id for PUT/PATCH logic
            
            // Ensure boolean values are properly handled in the payload
            const processedPayload = { ...payload } as Record<string, any>;
            if (collectionSchema?.paths) {
                Object.keys(collectionSchema.paths).forEach((key) => {
                    const fieldSchema = collectionSchema.paths[key];
                    if (fieldSchema?.instance === 'Boolean') {
                        // Ensure boolean fields are explicitly set to true/false, not undefined
                        if (processedPayload[key] === undefined || processedPayload[key] === null) {
                            processedPayload[key] = false;
                        }
                    }
                });
            }
            

            
            let url = COLLECTION_USERS_URL + `/${collectionName}`;
            let method = "PUT"; // Default for new record (create or full replace)

            if (isEditing) {
                url = `${COLLECTION_USERS_URL}/${collectionName}/${_id}`;
                method = "PATCH"; // Use PATCH for partial update of existing record
            }

            // Use ApiClient instead of fetch (automatically includes Authorization header)
            if (isEditing) {
                await apiClient.patch(`/iu/ds/cmp/set/entry/${collectionName}/${_id}`, processedPayload);
            } else {
                await apiClient.put(`/iu/ds/cmp/set/entry/${collectionName}`, processedPayload);
            }
            
            setCollectionUsersDataRefreshKey(prev => prev + 1); // Trigger DataTable refresh
            closeDialog();
            showMessage(`User ${isEditing ? 'updated' : 'created'} successfully!`, 'success');
        } catch (error) {
            Logger.error("Failed to save user:", error);
            showMessage(`Failed to save user: ${error instanceof Error ? error.message : String(error)}`, 'error');
        } finally {
            setLoading(false);
        }
    }, [currentCollectionUser, isEditing, collectionName, closeDialog, collectionSchema, showMessage]);

    // Validation functions
    const validateMobileNumber = useCallback((value: string): { isValid: boolean; errorMessage: string } => {
        if (!value) return { isValid: true, errorMessage: '' }; // Empty is valid (not required)
        
        if (!value.startsWith('+')) {
            return { isValid: false, errorMessage: 'Mobile number must contain country code (e.g., +1, +91)' };
        }
        
        // Basic validation for phone number format
        const phoneRegex = /^\+[1-9]\d{1,14}$/;
        if (!phoneRegex.test(value)) {
            return { isValid: false, errorMessage: 'Please enter a valid mobile number with country code' };
        }
        
        return { isValid: true, errorMessage: '' };
    }, []);

    const validateEmail = useCallback((value: string): { isValid: boolean; errorMessage: string } => {
        if (!value) return { isValid: true, errorMessage: '' }; // Empty is valid (not required)
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            return { isValid: false, errorMessage: 'Please enter a valid email address' };
        }
        
        return { isValid: true, errorMessage: '' };
    }, []);

    // Handle changes in dialog form fields
    const handleFieldChange = useCallback((key: string, value: any) => {
        setCurrentCollectionUser((prev) => {
            if (!prev) return null;
            
            // Ensure boolean values are properly handled
            let processedValue = value;
            if (typeof value === 'boolean') {
                processedValue = value; // Keep boolean as is
            } else if (value === '') {
                // For empty strings, check if the field should be boolean
                const fieldSchema = collectionSchema?.paths?.[key];
                if (fieldSchema?.instance === 'Boolean') {
                    processedValue = false; // Set to false for boolean fields
                }
            }
            
            return { ...prev, [key]: processedValue };
        });

        // Validate fields and update validation state
        if (key === 'primaryMobile') {
            const mobileValidation = validateMobileNumber(value);
            setValidationErrors(prev => ({
                ...prev,
                [key]: mobileValidation.isValid ? '' : mobileValidation.errorMessage
            }));
        } else if (key === 'primaryEmail') {
            const emailValidation = validateEmail(value);
            setValidationErrors(prev => ({
                ...prev,
                [key]: emailValidation.isValid ? '' : emailValidation.errorMessage
            }));
        }
    }, [collectionSchema, validateMobileNumber, validateEmail]);

    // Check if there are any validation errors
    const hasValidationErrors = useCallback(() => {
        return Object.values(validationErrors).some(error => error !== '');
    }, [validationErrors]);

    const handleTileTouchStart = useCallback(
        (e: React.TouchEvent, user: any) => {
            if (!isMobile) return;
            const t = e.touches[0];
            setMobileSwipeRow((prev) => {
                const anotherWasOpen = Boolean(
                    prev && prev.userId !== user._id && Math.abs(prev.offset) > 0.5
                );
                const baseOffset =
                    !anotherWasOpen && prev?.userId === user._id ? prev!.offset : 0;
                swipeSessionRef.current = {
                    userId: user._id,
                    startX: t.clientX,
                    startY: t.clientY,
                    baseOffset,
                    lock: null,
                };
                latestSwipeOffsetRef.current = baseOffset;
                return { userId: user._id, offset: baseOffset, dragging: true };
            });
        },
        [isMobile]
    );

    const handleTileTouchMove = useCallback(
        (e: React.TouchEvent, user: any) => {
            if (!isMobile) return;
            const sess = swipeSessionRef.current;
            if (!sess || sess.userId !== user._id) return;
            const touch = e.touches[0];
            const dx = touch.clientX - sess.startX;
            const dy = touch.clientY - sess.startY;

            if (sess.lock === null) {
                if (
                    Math.abs(dx) > SWIPE_DIRECTION_LOCK_PX &&
                    Math.abs(dx) > Math.abs(dy) * 1.2
                ) {
                    sess.lock = 'h';
                } else if (
                    Math.abs(dy) > SWIPE_DIRECTION_LOCK_PX &&
                    Math.abs(dy) > Math.abs(dx) * 1.2
                ) {
                    sess.lock = 'v';
                }
            }
            if (sess.lock !== 'h') return;

            const raw = sess.baseOffset + dx;
            const clamped = clampSwipeOffset(
                raw,
                canEditWithRestrictions,
                canDeleteWithRestrictions,
                SWIPE_ACTION_WIDTH_PX
            );
            latestSwipeOffsetRef.current = clamped;
            setMobileSwipeRow({ userId: user._id, offset: clamped, dragging: true });
        },
        [isMobile, canEditWithRestrictions, canDeleteWithRestrictions]
    );

    const handleTileTouchEnd = useCallback(
        (e: React.TouchEvent, user: any) => {
            if (!isMobile) return;
            const sess = swipeSessionRef.current;
            if (!sess || sess.userId !== user._id) {
                swipeSessionRef.current = null;
                return;
            }
            const t = e.changedTouches[0];
            const dx = t.clientX - sess.startX;
            const dy = t.clientY - sess.startY;
            const { lock, baseOffset } = sess;
            swipeSessionRef.current = null;

            const finishHorizontalSnap = (off: number) => {
                const snapped = computeSnappedOffset(
                    off,
                    canEditWithRestrictions,
                    canDeleteWithRestrictions,
                    SWIPE_ACTION_WIDTH_PX
                );
                latestSwipeOffsetRef.current = snapped;
                setMobileSwipeRow({ userId: user._id, offset: snapped, dragging: false });
            };

            // Fast flick: touchEnd may arrive with little or no touchMove
            if (
                lock === null &&
                Math.abs(dx) > Math.abs(dy) * 1.2 &&
                Math.abs(dx) > 18
            ) {
                const raw = baseOffset + dx;
                const clamped = clampSwipeOffset(
                    raw,
                    canEditWithRestrictions,
                    canDeleteWithRestrictions,
                    SWIPE_ACTION_WIDTH_PX
                );
                finishHorizontalSnap(clamped);
                return;
            }

            if (lock !== 'h') {
                if (Math.abs(dx) < 12 && Math.abs(dy) < 12) {
                    if (Math.abs(baseOffset) > 0.5) {
                        latestSwipeOffsetRef.current = 0;
                        setMobileSwipeRow({ userId: user._id, offset: 0, dragging: false });
                    } else {
                        openViewDialog(user);
                    }
                } else {
                    latestSwipeOffsetRef.current = baseOffset;
                    setMobileSwipeRow({
                        userId: user._id,
                        offset: baseOffset,
                        dragging: false,
                    });
                }
                return;
            }

            finishHorizontalSnap(latestSwipeOffsetRef.current);
        },
        [isMobile, canEditWithRestrictions, canDeleteWithRestrictions, openViewDialog]
    );

    const handleTileTouchCancel = useCallback(
        (_e: React.TouchEvent, user: any) => {
            if (!isMobile) return;
            const sess = swipeSessionRef.current;
            if (!sess || sess.userId !== user._id) return;
            swipeSessionRef.current = null;
            const snapped = computeSnappedOffset(
                latestSwipeOffsetRef.current,
                canEditWithRestrictions,
                canDeleteWithRestrictions,
                SWIPE_ACTION_WIDTH_PX
            );
            latestSwipeOffsetRef.current = snapped;
            setMobileSwipeRow({ userId: user._id, offset: snapped, dragging: false });
        },
        [isMobile, canEditWithRestrictions, canDeleteWithRestrictions]
    );

    return (
        <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
            {loading && ( // Render backdrop and spinner if loading
                <Backdrop
                    sx={(theme) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
                    open={loading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
            <DynamicBreadcrumbs items={[
                { itemPath: "/dashboard", itemLabel: "Home" },
                { itemPath: "/instant-updates/data-sets", itemLabel: "Data Sets" },
                { itemPath: `/instant-updates/data-sets/${collectionName}`, itemLabel: collectionName ? collectionName : "No Collection" }
            ]} />
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" gutterBottom>
                    All Users in "{collectionName ? collectionName : "No Collection"}"
                </Typography>
            </Box>

            {/* Responsive Toolbar Container */}
            <Box 
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 2 },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    mb: 3,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
                }}
            >
                {/* Desktop View - Full Toolbar */}
                {!isMobile ? (
                    <>
                        {/* Left side - CSV Manager */}
                        {canManageCsv && (
                            <Box 
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: { xs: 1, sm: 1 },
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    flexGrow: 1
                                }}
                            >
                                <CsvManager 
                                    collectionName={collectionName ? collectionName : "No Collection"} 
                                    canUpload={canManageCsv}
                                    canDownload={canManageCsv}
                                    onUploadComplete={() => setCollectionUsersDataRefreshKey(k => k + 1)}
                                />
                            </Box>
                        )}

                        {/* Right side - Action Buttons */}
                        <Box 
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: { xs: 1, sm: 1 },
                                alignItems: { xs: 'stretch', sm: 'center' },
                                minWidth: { xs: '100%', sm: 'auto' }
                            }}
                        >
                            {canCreate && (
                                <Button 
                                    variant="contained" 
                                    color="secondary" 
                                    startIcon={<Add />} 
                                    onClick={() => openDialog(undefined)}
                                    size="small"
                                    sx={{ 
                                        minWidth: { xs: '100%', sm: 'auto' },
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    New User
                                </Button>
                            )}
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(_e, newMode) => {
                                    if (newMode !== null) {
                                        setViewMode(newMode);
                                        localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
                                    }
                                }}
                                size="small"
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        '&.Mui-selected': {
                                            backgroundColor: (theme) => theme.palette.toggle?.selectedBg ?? (theme.palette.mode === 'dark' ? '#191f45' : theme.palette.secondary.main),
                                            color: (theme) => theme.palette.toggle?.selectedColor ?? (theme.palette.mode === 'dark' ? '#fff' : theme.palette.secondary.contrastText),
                                            '&:hover': { backgroundColor: (theme) => theme.palette.toggle?.selectedHoverBg ?? (theme.palette.mode === 'dark' ? '#232a54' : theme.palette.secondary.dark) }
                                        }
                                    }
                                }}
                            >
                                <ToggleButton value="tile" aria-label="Tile view">
                                    <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
                                </ToggleButton>
                                <ToggleButton value="table" aria-label="Table view">
                                    <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </>
                ) : (
                    /* Mobile/Tablet View - Icon Buttons Only */
                    <Box 
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            gap: 2
                        }}
                    >
                        {/* Upload Data Icon Button - Left Side */}
                        {canManageCsv && (
                            <IconButton
                                color="secondary"
                                onClick={() => setUploadDialogOpen(true)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'secondary.main',
                                    color: 'secondary.main',
                                    '&:hover': {
                                        backgroundColor: 'secondary.main',
                                        color: 'secondary.contrastText',
                                    },
                                    p: 1.5,
                                    minWidth: 48,
                                    minHeight: 48
                                }}
                                title="Upload Data"
                            >
                                <CloudUpload />
                            </IconButton>
                        )}

                        {/* New User Icon Button - Right Side */}
                        {canCreate && (
                            <IconButton
                                color="secondary"
                                onClick={() => openDialog(undefined)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'secondary.main',
                                    color: 'secondary.main',
                                    '&:hover': {
                                        backgroundColor: 'secondary.main',
                                        color: 'secondary.contrastText',
                                    },
                                    p: 1.5,
                                    minWidth: 48,
                                    minHeight: 48
                                }}
                                title="New User"
                            >
                                <Add />
                            </IconButton>
                        )}
                    </Box>
                )}
            </Box>
            
            {viewMode === 'table' ? (
                <DataTableWithDynamicSchema
                    dataUrl={COLLECTION_USERS_URL + `/${collectionName}`}
                    schemaUrl={GET_COLLECTION_SCHEMA_URL + `/${collectionName}`}
                    columnsToExclude={['_id', 'createdAt', 'updatedAt', '__v']}
                    onDelete={deleteCollectionUser}
                    onEdit={openDialog}
                    onView={openViewDialog}
                    refreshTrigger={collectionUsersDataRefreshKey}
                    onSchemaLoaded={handleSchemaLoaded}
                    canEdit={canEditWithRestrictions}
                    canDelete={canDeleteWithRestrictions}
                    dataSetName={collectionName}
                />
            ) : (
                tileLoading ? (
                    <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress sx={(t: any) => ({ color: t.palette.secondary.main })} />
                    </Box>
                ) : (
                    <>
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search contacts..."
                            value={tileSearchQuery}
                            onChange={(e) => setTileSearchQuery(e.target.value)}
                            sx={{ width: 320 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    {filteredTileData.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                            {tileData.length === 0 ? 'No users found.' : 'No contacts match your search.'}
                        </Typography>
                    ) : (
                    <>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 280px))',
                            gap: 2,
                            justifyContent: isMobile ? 'center' : 'normal',
                        }}
                    >
                        {filteredTileData.slice(0, tileVisibleCount).map((user: any) => {
                            const rowOffset =
                                isMobile && mobileSwipeRow?.userId === user._id
                                    ? mobileSwipeRow!.offset
                                    : 0;
                            const rowDragging =
                                isMobile &&
                                mobileSwipeRow?.userId === user._id &&
                                mobileSwipeRow!.dragging;

                            const tileContent = (
                                <>
                                    <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
                                        <Person sx={{ color: tileUserIconColor, fontSize: 28 }} />
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ fontWeight: 700, wordBreak: 'break-word', lineHeight: 1.3 }}
                                        >
                                            {[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}
                                        </Typography>
                                    </Box>
                                    {user.primaryMobile && (
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {user.primaryMobile}
                                            </Typography>
                                        </Box>
                                    )}
                                    {user.primaryEmail && (
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {user.primaryEmail}
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            );

                            if (!isMobile) {
                                return (
                                    <Box
                                        key={user._id}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => openViewDialog(user)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                openViewDialog(user);
                                            }
                                        }}
                                        sx={{
                                            borderRadius: 2.5,
                                            overflow: 'hidden',
                                            position: 'relative',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            backgroundColor: 'background.paper',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            p: 2,
                                            gap: 1,
                                            minHeight: 120,
                                            cursor: 'pointer',
                                            transition: 'box-shadow 0.2s, border-color 0.2s',
                                            '&:hover': {
                                                borderColor: 'secondary.main',
                                                boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                                            },
                                            '&:hover .tile-actions': { opacity: 1 },
                                        }}
                                    >
                                        {tileContent}
                                        <Box
                                            className="tile-actions"
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                px: 2,
                                                py: 1,
                                                background: (t) =>
                                                    t.palette.mode === 'dark'
                                                        ? 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 70%, transparent 100%)'
                                                        : 'linear-gradient(to top, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.7) 70%, transparent 100%)',
                                                opacity: 0,
                                                transition: 'opacity 0.25s ease',
                                            }}
                                        >
                                            {canEditWithRestrictions && (
                                                <Tooltip title="Edit">
                                                    <Button
                                                        color="secondary"
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openDialog(user);
                                                        }}
                                                        sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {canDeleteWithRestrictions && (
                                                <Tooltip title="Delete">
                                                    <Button
                                                        color="error"
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            deleteCollectionUser(user);
                                                        }}
                                                        sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            }

                            return (
                                <Box
                                    key={user._id}
                                    sx={{
                                        borderRadius: 2.5,
                                        overflow: 'hidden',
                                        position: 'relative',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        minHeight: 120,
                                        touchAction: 'pan-y',
                                        isolation: 'isolate',
                                    }}
                                >
                                    {/* Underlay: full-bleed action strips (Messages-style) */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'stretch',
                                            zIndex: 0,
                                            borderRadius: 2.5,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {canEditWithRestrictions && (
                                            <Box
                                                sx={{
                                                    width: SWIPE_ACTION_WIDTH_PX,
                                                    flexShrink: 0,
                                                    bgcolor: 'secondary.main',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        aria-label="Edit user"
                                                        size="medium"
                                                        sx={{
                                                            color: 'secondary.contrastText',
                                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' },
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMobileSwipeRow(null);
                                                            openDialog(user);
                                                        }}
                                                    >
                                                        <Edit fontSize="medium" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        )}
                                        <Box sx={{ flex: 1, minWidth: 0 }} />
                                        {canDeleteWithRestrictions && (
                                            <Box
                                                sx={{
                                                    width: SWIPE_ACTION_WIDTH_PX,
                                                    flexShrink: 0,
                                                    bgcolor: 'error.main',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        aria-label="Delete user"
                                                        size="medium"
                                                        sx={{
                                                            color: 'error.contrastText',
                                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' },
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMobileSwipeRow(null);
                                                            deleteCollectionUser(user);
                                                        }}
                                                    >
                                                        <Delete fontSize="medium" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Sliding card (follows finger, snaps with spring-like easing) */}
                                    <Box
                                        role="button"
                                        tabIndex={0}
                                        onTouchStart={(e) => handleTileTouchStart(e, user)}
                                        onTouchMove={(e) => handleTileTouchMove(e, user)}
                                        onTouchEnd={(e) => handleTileTouchEnd(e, user)}
                                        onTouchCancel={(e) => handleTileTouchCancel(e, user)}
                                        sx={{
                                            position: 'relative',
                                            zIndex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            p: 2,
                                            gap: 1,
                                            minHeight: 120,
                                            cursor: 'pointer',
                                            backgroundColor: 'background.paper',
                                            borderRadius: 2.5,
                                            transform: `translateX(${rowOffset}px)`,
                                            transition: rowDragging
                                                ? 'none'
                                                : 'transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                            willChange: rowDragging ? 'transform' : 'auto',
                                            boxShadow: rowOffset !== 0 ? 3 : 'none',
                                        }}
                                    >
                                        {tileContent}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                    {tileVisibleCount < filteredTileData.length && (
                        <Box display="flex" justifyContent="center" mt={3}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => setTileVisibleCount((prev) => prev + TILE_PAGE_SIZE)}
                            >
                                Load More ({filteredTileData.length - tileVisibleCount} remaining)
                            </Button>
                        </Box>
                    )}
                    </>
                    )}
                    </>
                )
            )}

            {/* Dialog for Add/Edit/View User */}
            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth>
                <DialogTitle>
                    {isViewMode ? "View User Data" : isEditing ? "Edit User Data" : "Add New User"}
                </DialogTitle>
                <DialogContent>
                    {/* Show timestamps for existing records */}
                    {currentCollectionUser && currentCollectionUser._id && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Record Information
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {(currentCollectionUser as any).createdAt && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            Created:
                                        </Typography>
                                        <Typography variant="body2">
                                            {new Date((currentCollectionUser as any).createdAt).toLocaleString('en-GB', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit',
                                                timeZoneName: 'short'
                                            })}
                                        </Typography>
                                    </Box>
                                )}
                                {(currentCollectionUser as any).updatedAt && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            Last Updated:
                                        </Typography>
                                        <Typography variant="body2">
                                            {new Date((currentCollectionUser as any).updatedAt).toLocaleString('en-GB', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit',
                                                timeZoneName: 'short'
                                            })}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                    {/* Render fields dynamically based on the fetched schema */}
                    {currentCollectionUser && collectionSchema?.paths ? (
                        <>
                            {Object.keys(collectionSchema.paths).filter(key => {
                                // Exclude standard excluded fields
                                if (excludedFields.includes(key)) return false;
                                return true;
                            }).map((key: string) => {
                                const fieldSchema = collectionSchema.paths[key];
                                // Determine input type based on schema instance
                                let inputType = 'text';
                                let multiline = false;
                                let rows = 1;
                                const isRequired = fieldSchema?.isRequired === true; // Explicitly check for true

                                switch (fieldSchema?.instance) {
                                    case 'Number': inputType = 'number'; break;
                                    case 'Boolean': inputType = 'checkbox'; break; // Render as checkbox
                                    case 'Date': inputType = 'datetime-local'; break;
                                    case 'String':
                                        if (key.toLowerCase().includes('description') || key.toLowerCase().includes('content')) {
                                            multiline = true;
                                            rows = 3;
                                        }
                                        break;
                                    default: inputType = 'text';
                                }

                                // Handle boolean fields with a Checkbox
                                if (inputType === 'checkbox') {
                                    return (
                                        <FormControlLabel
                                            key={key}
                                            control={
                                                <Checkbox
                                                    checked={Boolean((currentCollectionUser as any)?.[key])}
                                                    onChange={(e) => handleFieldChange(key, e.target.checked)}
                                                    disabled={key === "_id" || isViewMode} // Disable _id editing and in view mode
                                                />
                                            }
                                            label={CamelCaseToName(key)}
                                            sx={{ mt: 2, display: 'block' }} // Ensure it takes full width
                                        />
                                    );
                                }

                                // Determine if field is empty for validation
                                const isValueEmpty = (currentCollectionUser as any)?.[key] === undefined || (currentCollectionUser as any)?.[key] === null || (currentCollectionUser as any)?.[key] === '';
                                const hasError = isRequired && isValueEmpty;

                                // Special validation for primaryMobile and primaryEmail
                                const validationError = validationErrors[key] || '';
                                const hasValidationError = validationError !== '';

                                // Special handling for browserInfo object in Consent_Records dataset
                                if (collectionName === 'Consent_Records' && key === 'browserInfo') {
                                    const browserInfo = (currentCollectionUser as any)?.[key];
                                    return (
                                        <Box key={key} sx={{ mt: 2, mb: 2 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                {CamelCaseToName(key)}
                                            </Typography>
                                            {browserInfo ? (
                                                <Box sx={{ 
                                                    p: 2, 
                                                    border: '1px solid', 
                                                    borderColor: 'divider', 
                                                    borderRadius: 1,
                                                    backgroundColor: 'background.paper'
                                                }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        {browserInfo.userAgent && (
                                                            <Box>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    User Agent:
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                                    {browserInfo.userAgent}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {browserInfo.language && (
                                                            <Box>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    Language:
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {browserInfo.language}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {browserInfo.platform && (
                                                            <Box>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    Platform:
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {browserInfo.platform}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {browserInfo.cookieEnabled !== undefined && (
                                                            <Box>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    Cookies Enabled:
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {browserInfo.cookieEnabled ? 'Yes' : 'No'}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {browserInfo.onLine !== undefined && (
                                                            <Box>
                                                                <Typography variant="body2" color="textSecondary">
                                                                    Online Status:
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {browserInfo.onLine ? 'Online' : 'Offline'}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="textSecondary">
                                                    No browser information available
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                }

                                // Handle other field types with TextField
                                return (
                                    <TextField
                                        size="small"
                                        key={key}
                                        label={CamelCaseToName(key)}
                                        type={inputType}
                                        value={(currentCollectionUser as any)?.[key] || (inputType === 'number' ? 0 : '')}
                                        onChange={(e) => {
                                            const newValue = inputType === 'number' ? Number(e.target.value) : e.target.value;
                                            handleFieldChange(key, newValue);
                                        }}
                                        fullWidth
                                        variant="outlined"
                                        margin="normal" // Adds margin top and bottom
                                        disabled={key === "_id" || isViewMode} // Disable _id editing and in view mode
                                        required={isRequired && !isViewMode} // Don't show required in view mode
                                        error={(hasError || hasValidationError) && !isViewMode} // Don't show error in view mode
                                        helperText={
                                            !isViewMode 
                                                ? (hasValidationError ? validationError : (hasError ? `${CamelCaseToName(key)} is required` : ''))
                                                : ''
                                        }
                                        multiline={multiline}
                                        rows={rows}
                                    />
                                );
                            })}
                            

                        </>
                    ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                            Loading schema or no schema available for dynamic form generation.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {isViewMode ? (
                        <>
                            <Button 
                                variant="outlined" 
                                color="secondary" 
                                onClick={closeDialog}
                            >
                                Close
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button 
                                variant="outlined" 
                                color="secondary" 
                                onClick={closeDialog} 
                                startIcon={<Cancel />}
                            >
                                Cancel
                            </Button>
                            <Button 
                                onClick={saveCollectionUser} 
                                startIcon={<Save />} 
                                variant="contained" 
                                color="success"
                                disabled={hasValidationErrors()}
                            >
                                Save
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>



            {/* Upload Data Dialog for Mobile/Tablet */}
            <Dialog 
                open={uploadDialogOpen} 
                onClose={() => setUploadDialogOpen(false)} 
                fullWidth 
                maxWidth="sm"
            >
                <DialogTitle>
                    Upload Data
                </DialogTitle>
                <DialogContent>
                    {canManageCsv && (
                        <Box sx={{ mt: 2 }}>
                            <CsvManager 
                                collectionName={collectionName ? collectionName : "No Collection"} 
                                canUpload={canManageCsv}
                                canDownload={canManageCsv}
                                onUploadComplete={() => setCollectionUsersDataRefreshKey(k => k + 1)}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={() => setUploadDialogOpen(false)}
                        startIcon={<Cancel />}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmDialogOpen}
                onClose={() => setDeleteConfirmDialogOpen(false)}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography id="delete-dialog-description" mt={2}>
                        Are you sure you want to delete user "{recordToDelete?.email || recordToDelete?.phoneNumber || recordToDelete?._id}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmDialogOpen(false)} variant="outlined" color="secondary" startIcon={<Cancel />}>
                        Cancel
                    </Button>
                    <Button onClick={async () => {
                        try {
                            setLoading(true);
                            const recordId = recordToDelete?._id;
                            if (!recordId) {
                                throw new Error('Record ID not found');
                            }
                            // Use ApiClient instead of fetch (automatically includes Authorization header)
                            await apiClient.delete(`/iu/ds/cmp/set/entry/${collectionName}/${recordId}`);
                            setCollectionUsersDataRefreshKey(prev => prev + 1); // Trigger DataTable refresh
                            setDeleteConfirmDialogOpen(false);
                            showMessage('User deleted successfully!', 'success');
                        } catch (error) {
                            Logger.error("Failed to delete user:", error);
                            showMessage(`Failed to delete user: ${error instanceof Error ? error.message : String(error)}`, 'error');
                        } finally {
                            setLoading(false);
                        }
                    }} color="error" variant="outlined">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
